import { Checkbox, message, TableColumnsType } from 'antd';
import {
  Button,
  Footer,
  Loading,
  TitleBar,
} from '../../../../components';
import TableV3 from '../../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../../utils';
import { useNavigate, useParams } from 'react-router';
import { useState } from 'react';

import { ApolloError, useMutation, useQuery } from '@apollo/client';
import QueryErrorBoundary from '../../../../components/error-handling';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { debounce } from 'lodash';
import {
  GET_PROCESS_AREAS_DASHBOARD,
  UPDATE_PROCESS_AREA
} from './queries';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import FinancialData from '../../../../components/common/FinancialData';
import { ProcessArea } from './types';
import { useLocationName } from '../../../../hooks';
import {ProcessAreaAndTaggingTitle}  from '../../constants';

type ProcessAreaDashboard = {
  uuid: string;
  processArea: string;
  applicationsTagged: number;
  revenueSupported: number;
  supportedByITApplication: boolean;
};

type ProcessAreasDashboard = {
  getProcessAreaDashboard: ProcessAreaDashboard[];
};

const LocationProcessAreasDashboard = () => {
  const { locationId } = useParams();
  const [checkedProcessAreas, setCheckedProcessAreas] = useState<{
    [key: string]: boolean;
  }>({});

  const navigate = useNavigate();
  const { data, loading: qfLoading, error: qfError, refetch } = useQuery<ProcessAreasDashboard>(
    GET_PROCESS_AREAS_DASHBOARD,
    {
      variables: {
        locationUUID: locationId
      },
      onCompleted: (data) => {
        const initialState: { [key: string]: boolean } = {};
        data?.getProcessAreaDashboard.forEach((processArea) => {
          initialState[processArea.uuid] =
            processArea.supportedByITApplication;
        });
        setCheckedProcessAreas(initialState);
      },
    },
  );

  const {locationName, isLocationNameLoading, locationNameError } = useLocationName(locationId);

  const [updateSupportAppDependency, { loading: updateCheckboxLoading }] =
    useMutation(UPDATE_PROCESS_AREA, {
      onCompleted: refetch
    });

  const processAreasData = data?.getProcessAreaDashboard ?? [];
  const orderedProcessAreasData = processAreasData?.toSorted(
    (a, b) => Number(b?.processArea) - Number(a?.processArea),
  );

  const loadingStates = qfLoading || updateCheckboxLoading;

  const handleCheckboxToggle = async (e: CheckboxChangeEvent, uuid: string) => {
    const currentValue = e.target.checked;

    try {
      const response = await updateSupportAppDependency({
        variables: {
          data: {
            uuid,
            supportedByITApplication: currentValue,
          },
        },
      });

      if (response.data) {
        message.success('Changes Saved');
        setCheckedProcessAreas((prev) => ({
          ...prev,
          [uuid]: currentValue,
        }));
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        message.error(e.message);
      }
    }
  };

  const debouncedHandleCheckbox = debounce(
    (e: CheckboxChangeEvent, uuid: string) => {
      handleCheckboxToggle(e, uuid);
    },
    300,
  );

  const columns: TableColumnsType<ProcessAreaDashboard> = [
    {
      title: '',
      width: 450,
      dataIndex: 'processArea',
      key: 'processArea',
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.processArea, b.processArea),
      render: (text: string) => (
        <span className="custom-table--column ml-10 flex gap-2">
          {text}
        </span>
      ),
    },
    {
      title: (
        <span>
          Revenue <br />
          Supported
          <br />
          Annually
        </span>
      ),
      width: 142,
      dataIndex: 'revenueSupported',
      key: 'revenueSupported',
      fixed: 'left',
      align: 'right',
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenueSupported">
          {<FinancialData text={text} formatType="decimal" />}
        </span>
      ),
    },
    {
      title: '# of Applications Tagged',
      width: 140,
      dataIndex: 'applicationsTagged',
      key: 'applicationsTagged',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(a.applicationsTagged, b.applicationsTagged),
      render: (applicationsTagged: number) => (
        <span className="custom-table--column">{applicationsTagged ?? 0}</span>
      ),
    },
    {
      title: 'No Application Dependency',
      width: 75,
      dataIndex: 'applicationDependency',
      key: 'applicationDependency',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(
          a.supportedByITApplication,
          b.supportedByITApplication,
        ),
      render: (_, record) => {
        const { uuid, applicationsTagged } = record;

        const isChecked =
          checkedProcessAreas[uuid] && !applicationsTagged;

        return (
          <span className="custom-table--column">
            <Checkbox
              onChange={(checkedValue) => {
                if (checkedValue && applicationsTagged > 0) {
                  message.error('Process Area has IT applications mapped');
                  return;
                }
                debouncedHandleCheckbox(checkedValue, uuid);
              }}
              checked={isChecked}
              defaultChecked={isChecked}
            />
          </span>
        );
      },
    },
  ];

  const extendActions = (record: ProcessArea) => {
    return (
      <div className="relative w-full h-7">
        {!checkedProcessAreas[record.uuid] ? (
          <Button
            type="primary-rounded"
            className="w-full text-sm font-semibold min-w-[80px]"
            onClick={() =>
              navigate(
                `/cyber/location-level/${locationId}/process-areas-and-processes/${record.uuid}/it-application-selection`
              )
            }
          >
            Tag Applications
          </Button>
        ) : null}
      </div>
    );
  };

  const handleApplicationSummaryView = (
  <Button
      type="primary"
      className="w-[222px] text-sm !p-1.5 mt-[4.5rem] h-fit self-center"
      onClick={() =>
        navigate(
          `/cyber/location-level/${locationId}/process-areas-and-processes/summary`
        )
      }
    >
      Application Summary View
    </Button>)

  let headerContent;

  if (isLocationNameLoading) {
      headerContent = <Loading />;
    } else {
      headerContent = (
        <TitleBar
        title={ProcessAreaAndTaggingTitle}
        subtitle={`${locationName} / Process Areas Overview`}
        brief={`Below is a list of existing Process Areas at the ${locationName} location. Process Areas can be physical production lines, utilities, etc.  that support operations.`}
        rightContent={handleApplicationSummaryView}
      />
      );
    }

  return (
    <div>
      <TitleUpdater title={ProcessAreaAndTaggingTitle} />
      <QueryErrorBoundary error={locationNameError}>{headerContent}</QueryErrorBoundary>
      <QueryErrorBoundary error={qfError}>
        <TableV3
          columns={columns}
          data={orderedProcessAreasData}
          loading={loadingStates}
          customStyles="custom-table--dependency-mapping"
          useBorderedSection
          editable
          editableOptions={{
            update: false,
          }}
          extendActions={extendActions}
        />
      </QueryErrorBoundary>
      <Footer backPath={`/cyber/location-level/${locationId}/application-mapping`} />
    </div>
  );
};

export default LocationProcessAreasDashboard;
